<template>
  <v-container v-scroll="onScroll" class="px-3 px-sm-6 mt-2" fluid>
    <v-row class="mb-12">
      <v-col cols="12">
        <v-row class="align-end" no-gutters>
          <v-col cols="12" sm="8" lg="10">
            <h1 class="text-h4 font-weight-bold">Community Awarded Gallery</h1>
            <h2 class="text-subtitle-1 mb-0">
              See recently nominated photos by our community
              <v-dialog v-model="galleryDialog" width="400">
                <template #activator="{ on }">
                  <v-btn icon small class="ml-2" v-on="on">
                    <v-icon small>fal fa-info-circle</v-icon>
                  </v-btn>
                </template>
                <v-card outlined>
                  <v-card-title class="text-h5" primary-title>
                    What is the Community Awarded Gallery?
                  </v-card-title>
                  <v-card-text>
                    <p>
                      Every day, our curation team selects photos for the Inspiration gallery.
                    </p>
                    <p>
                      The 100ASA community helps them in this process by nominating photos they think should receive an Inspiration star.
                    </p>
                    <p>
                      This is a great way to let our curation team know what you would like to see on the Inspiration gallery.
                    </p>
                    <p>
                      All nominated photos, will be shown on this gallery.
                    </p>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text small @click="galleryDialog = false">
                      Close
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </h2>
          </v-col>
          <v-col class="text-right" cols="12" sm="4" lg="2">
            <Filters
              :categories="categories"
              :on-apply="onApply"
              from="communityAwarded"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" class="px-0">
        <Gallery
          :photos="photos"
          :me="me"
          :load-more="loadMore"
          :auto-load-disabled="autoLoadDisabled"
          :loading="loading"
          :get-queries="getQueries"
          from="communityAwarded"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import goTo from 'vuetify/lib/services/goto'
import { mapState } from 'vuex'
import Gallery from '@/components/galleries/Gallery.vue'
import Filters from '@/components/galleries/Filters.vue'

export default {
  components: {
    Gallery,
    Filters
  },
  middleware: 'header',
  data() {
    return {
      loading: false,
      sheet: false,
      tags: this.$route.query.tags,
      hasTags: !!this.$route.query.tags,
      offsetTop: 0,
      galleryDialog: false
    }
  },
  async fetch({ store, $asa }) {
    try {
      await store.dispatch('account/getCategories')
    } catch (error) {
      $asa.appInsights('Pages: community-awarded.vue - method: fetch', error)
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'Community Awarded Gallery',
      description: "Have a look at the starting point of 100ASA's photo galleries."
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      photos: state => state.photos.communityAwardedList,
      isLastPage: state => state.photos.communityAwardedIsLastPage,
      page: state => state.photos.communityAwardedPage,
      categories: state => state.account.categories,
      selectedCategoryText: state => state.photos.selectedCategoryText,
      selectedTags: state => state.photos.selectedTags,
      loadDate: state => state.photos.communityAwardedGalleryLoadDate
    }),
    autoLoadDisabled() {
      return this.loading || this.photos.length === 0
    }
  },
  async mounted() {
    // Reload the gallery after 30 minutes in cache
    if (
      !this.loadDate ||
      moment().isAfter(moment(this.loadDate).add(30, 'minutes'))
    ) {
      await this.$store.dispatch('photos/resetCommunityAwardedGallery')
    }

    const reload = this.$asa.checkAndSetTagsAndCategory(
      this.selectedTags,
      this.selectedCategoryText
    )

    if (
      this.photos.length === 0 ||
      this.$route.query.back !== 'yes' ||
      (this.$route.query.tags &&
        this.$route.query.tags !== this.$store.state.photos.initialTags) ||
      (this.$route.query.category &&
        this.$route.query.category !==
          this.$store.state.photos.selectedCategoryText) ||
      reload
    ) {
      try {
        this.loading = true
        await this.$store.dispatch('photos/resetCommunityAwardedGallery')
        const data = {
          page: this.page,
          awarded: true,
          back: !!this.$route.query.back,
          from: 'communityAwarded'
        }
        await this.$store.dispatch('photos/getPhotos', data)
        const date = new Date()
        await this.$store.dispatch(
          'photos/setCommunityAwardedGalleryLoadDate',
          moment(date)
        )
      } catch (error) {
        this.$asa.appInsights('Pages: community-awarded.vue - method: mounted', error)
      } finally {
        this.loading = false
      }
    }
    if (
      this.$route.query.back === 'yes' &&
      this.$route.query.st &&
      this.photos.length > 0
    ) {
      goTo(Number(this.$route.query.st))
    }
  },
  methods: {
    async loadMore() {
      if (!this.isLastPage && this.photos.length !== 0 && !this.loading) {
        this.loading = true
        try {
          await this.$store.dispatch('photos/getPhotos', {
            page: this.page + 1,
            awarded: true,
            from: 'communityAwarded'
          })
        } catch (error) {
          this.$asa.appInsights('Pages: community-awarded.vue - method: loadMore', error)
        } finally {
          this.loading = false
        }
      }
    },
    getQueries() {
      const q = this.$route.query
      let query = ''
      query += `&st=${this.offsetTop}`
      if (q.category) query += `&category=${q.category}`
      if (q.tags) query += `&tags=${q.tags}`
      return query
    },
    onScroll(e) {
      setTimeout(() => {
        const top = document.getElementById('app').getBoundingClientRect().top * -1
        this.offsetTop = top
      })
    },
    async onApply(filters) {
      await this.$store.dispatch('photos/resetCommunityAwardedGallery')
      await this.$store.dispatch('photos/setFilters', filters)
      const data = {
        page: this.page,
        awarded: true,
        from: 'communityAwarded'
      }
      try {
        this.loading = true
        await this.$store.dispatch('photos/getPhotos', data)
        const date = new Date()
        await this.$store.dispatch(
          'photos/setCommunityAwardedGalleryLoadDate',
          moment(date)
        )
      } catch (error) {
        this.$asa.appInsights('Pages: community-awarded.vue - method: onApply', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
